// Step 2: the slot picker. A horizontally scrollable day rail and a grid of
// times for the selected day, all rendered in the visitor's chosen timezone.
// Handles loading, empty (no slots), and error states. The parent owns data
// fetching and timezone state; this component is presentational + selection.
// Implements: architecture.md v1.0 section 5 (GET /api/availability).

import { useEffect, useMemo, useState } from 'react';
import type { JSX } from 'react';
import type { BookingType, Slot } from '../api/types.js';
import {
  dayPillParts,
  formatLongDate,
  formatTime,
  groupSlotsByDay,
  shiftInstant,
} from '../lib/time.js';
import { Banner, SlotSkeletons, StateBlock } from './feedback.js';
import { TimeZoneSelect } from './TimeZoneSelect.js';

function durationLabel(min: number): string {
  return min < 60 ? `${min} min` : `${Math.floor(min / 60)} hr${min % 60 ? ` ${min % 60} min` : ''}`;
}

export function SlotPicker({
  bookingType,
  slots,
  tz,
  onTzChange,
  status,
  errorMessage,
  onSelect,
  onRetry,
  busy = false,
}: {
  bookingType: BookingType;
  slots: Slot[];
  tz: string;
  onTzChange: (tz: string) => void;
  status: 'loading' | 'ready' | 'error';
  errorMessage?: string;
  onSelect: (slot: Slot) => void;
  onRetry?: () => void;
  /** When true a selection is in flight: disable every slot so a double-click
   *  cannot fire a second request against a token the first may have rotated. */
  busy?: boolean;
}): JSX.Element {
  const days = useMemo(() => groupSlotsByDay(slots, tz), [slots, tz]);
  const [activeDay, setActiveDay] = useState<string | null>(null);

  // A front buffer (dead time at the start of the meeting, inside the reserved
  // slot) is shown to the customer as the ACTUAL meeting start. The label below
  // renders slot.start + frontBufferMin; the value submitted stays the nominal
  // slot.start (onSelect passes the unmodified Slot). Absent or 0 = no shift.
  const frontBufferMin = bookingType.frontBufferMin ?? 0;

  // Keep the active day valid as data/timezone changes: default to the first
  // day that has slots; if the current selection vanished, fall back.
  useEffect(() => {
    if (days.length === 0) {
      setActiveDay(null);
      return;
    }
    setActiveDay((prev) => {
      if (prev && days.some((d) => d.key === prev)) return prev;
      return days[0]!.key;
    });
  }, [days]);

  const activeGroup = days.find((d) => d.key === activeDay) ?? null;

  return (
    <section className="picker rise" aria-label="Choose a time">
      <div className="picker-head">
        <div className="picker-summary">
          <span className="ps-name">{bookingType.name}</span>
          <span className="ps-dur">· {durationLabel(bookingType.durationMin)}</span>
        </div>
        <TimeZoneSelect value={tz} onChange={onTzChange} />
      </div>

      {status === 'error' ? (
        <div className="slot-area">
          <Banner kind="error">{errorMessage ?? 'Could not load availability.'}</Banner>
          {onRetry ? (
            <div style={{ marginTop: 'var(--space-4)' }}>
              <button type="button" className="btn btn-ghost" onClick={onRetry}>
                Try again
              </button>
            </div>
          ) : null}
        </div>
      ) : status === 'loading' ? (
        <div className="slot-area">
          <SlotSkeletons count={9} />
        </div>
      ) : days.length === 0 ? (
        <div className="slot-area">
          <StateBlock title="No times available">
            There are no open slots in this window. Try a different booking type, or check back
            soon.
          </StateBlock>
        </div>
      ) : (
        <>
          {/* A group of toggle buttons, not a tablist: the day rail switches
              which day's grid is shown, but the grid is not a WAI-ARIA tabpanel
              (no aria-controls / roving tabindex / arrow-key navigation). Using
              aria-pressed is honest about the interaction and needs no keyboard
              choreography we do not implement. */}
          <div className="day-nav" role="group" aria-label="Available days">
            {days.map((d) => {
              const parts = dayPillParts(d.sample, tz);
              const isActive = d.key === activeDay;
              return (
                <button
                  key={d.key}
                  type="button"
                  aria-pressed={isActive}
                  className={`day-pill ${isActive ? 'is-active' : ''}`}
                  onClick={() => setActiveDay(d.key)}
                >
                  <span className="dp-dow">{parts.dow}</span>
                  <span className="dp-day">{parts.day}</span>
                  <span className="dp-mon">{parts.month}</span>
                  <span className="dp-count">
                    {d.slots.length} {d.slots.length === 1 ? 'slot' : 'slots'}
                  </span>
                </button>
              );
            })}
          </div>

          <div className="slot-area">
            {activeGroup ? (
              <>
                <div className="slot-day-label">{formatLongDate(activeGroup.sample, tz)}</div>
                <div className="slot-grid">
                  {activeGroup.slots.map((s) => (
                    // key + onSelect stay on the NOMINAL slot; only the label is
                    // shifted by the front buffer (display-only transform).
                    <button
                      key={s.start}
                      type="button"
                      className="slot-btn"
                      disabled={busy}
                      onClick={() => {
                        if (busy) return;
                        onSelect(s);
                      }}
                    >
                      {formatTime(shiftInstant(s.start, frontBufferMin), tz)}
                    </button>
                  ))}
                </div>
              </>
            ) : null}
          </div>
        </>
      )}
    </section>
  );
}
